/**
 * #416 — "this screen is not showing you everything" indicator.
 *
 * A hazard console that fails to an empty state is indistinguishable from a
 * hazard console with nothing to report. This names the feeds that did not
 * load so the operator can tell an outage from a quiet shift.
 *
 * Deliberately NOT styled as a hazard: critical red is reserved for real
 * client risk. This is an infrastructure problem, so it reads as a system
 * warning — an operator must never confuse a dead feed with an emergency.
 *
 * Renders nothing when every feed is healthy.
 */
import React from "react";
import PropTypes from "prop-types";
import { theme, severityToken } from "../../styles/designTokens";

export default function FeedStatusNote({ degraded = [] }) {
  const names = (Array.isArray(degraded) ? degraded : [])
    .map((feed) => (feed && typeof feed.name === "string" ? feed.name.trim() : ""))
    .filter(Boolean);
  if (names.length === 0) return null;
  return (
    <section
      // Assertive: an operator acting on an incomplete hazard picture is the
      // failure this exists to prevent, so it interrupts.
      role="alert"
      aria-label="Degraded data feed"
      style={{
        margin: "0 0 12px",
        padding: "10px 14px",
        background: theme.surface,
        border: `1px solid ${theme.border}`,
        borderLeft: `3px solid ${severityToken("atRisk").color}`,
        borderRadius: 6,
        color: theme.text,
        fontSize: 13,
        lineHeight: 1.45,
      }}
    >
      <strong style={{ fontWeight: 600 }}>
        {names.length === 1 ? "A data feed did not load" : "Data feeds did not load"}
      </strong>
      <span style={{ color: theme.textMuted }}>
        {" "}
        — {names.join(", ")}. This screen may be incomplete; treat an empty
        result as unknown rather than clear.
      </span>
    </section>
  );
}

FeedStatusNote.propTypes = {
  degraded: PropTypes.arrayOf(PropTypes.object),
};
